import { CheckCircleIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { Link as ChakraLink, Text, useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import updateGCCHServiceStatus from 'API/Service/updateGCCHServiceStatus';

import {
    Button,
    MenuItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from 'components/v4';

const GCCHMenuItems = ({ cardStatus, darkMode, service }) => {
    const modalState = useDisclosure();
    const { colorScheme } = useSelector((state) => state.settings);
    const queryClient = useQueryClient();
    const [isUpdating, setIsUpdating] = useState(false);

    const handleConfirmActivation = async () => {
        setIsUpdating(true);
        try {
            // Update service status to 0 (Healthy)
            await updateGCCHServiceStatus(service.id);

            // Refresh the consolidated dashboard payload only.
            await queryClient.invalidateQueries(['dashboardServices']);

            modalState.onClose();
        } catch (error) {
            console.error('Failed to update service status:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    // Only show menu items if status is 14 (Pending Activation)
    if (!cardStatus?.showActivationMenu) {
        return null;
    }

    return (
        <>
            {/* Activation Guide Menu Item */}
            <MenuItem
                id="gcch-activation-guide"
                icon={<ExternalLinkIcon color={darkMode ? 'white' : 'black'} />}
                onClick={() =>
                    window.open(
                        '/ui/Guides/GCCH_Activation.pdf',
                        '_blank',
                        'noopener,noreferrer',
                    )
                }
                data-walkthroughid={`/dashboard-services/card/${service.name}/activation-guide`}>
                <Text marginBottom="0">Activation Guide</Text>
            </MenuItem>

            {/* Confirm Activation Menu Item */}
            <MenuItem
                id="gcch-confirm-activation"
                icon={<CheckCircleIcon color={darkMode ? 'white' : 'black'} />}
                onClick={modalState.onOpen}
                data-walkthroughid={`/dashboard-services/card/${service.name}/confirm-activation`}>
                <Text marginBottom="0">Confirm Activation</Text>
            </MenuItem>

            {/* Confirmation Modal */}
            <Modal
                closeOnOverlayClick={false}
                isCentered
                isOpen={modalState.isOpen}
                onClose={modalState.onClose}
                size="md">
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Confirm Activation</ModalHeader>
                    <ModalBody>
                        <Text className="text-base">
                            Please confirm required activation steps have been
                            completed{' '}
                            <ChakraLink
                                href="/ui/Guides/GCCH_Activation.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                color={`${colorScheme}.500`}
                                textDecoration="underline">
                                (view guide)
                            </ChakraLink>
                            . Failure to do so, may result in calling services
                            failing.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="secondary mr-4 !w-full"
                            onClick={modalState.onClose}
                            isDisabled={isUpdating}>
                            Cancel
                        </Button>
                        <Button
                            bg={`${colorScheme}.500`}
                            _hover={{
                                bg: `${colorScheme}.600`,
                            }}
                            className={`flex min-h-[38px] !w-full items-center justify-center rounded-md px-3 py-1.5 text-base font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--chakra-colors-${colorScheme}-600)]`}
                            onClick={handleConfirmActivation}
                            isLoading={isUpdating}
                            loadingText="Updating...">
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default GCCHMenuItems;
